import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { api, DisplayPreferencesAPI } from '../services/api';

export type CurrencyCode = 'USD' | 'EUR' | 'GBP' | 'ZAR';
export type ThemeMode = 'dark' | 'shadcn-zinc' | 'midnight-blue' | 'terminal-green' | 'warm-dark' | 'snow' | 'dracula' | 'nord' | 'ocean' | 'solarized-dark' | 'rose';
export type AccentColor = 'teal' | 'blue' | 'gold' | 'green' | 'violet';
export type FontSize = 'small' | 'medium' | 'large';
export type ChartColorScheme = 'classic' | 'high-contrast' | 'colorblind';
export type AnimationIntensity = 'full' | 'reduced' | 'none';

interface DisplayPreferences {
  currency: CurrencyCode;
  theme: ThemeMode;
  compactMode: boolean;
  accentColor: AccentColor;
  fontSize: FontSize;
  chartColorScheme: ChartColorScheme;
  sidebarCollapsed: boolean;
  animationIntensity: AnimationIntensity;
  showPriceTicker: boolean;
}

interface NotificationPreferences {
  priceAlerts: boolean;
  portfolioUpdates: boolean;
  marketNews: boolean;
  emailDigest: boolean;
}

interface UserPreferences {
  display: DisplayPreferences;
  notifications: NotificationPreferences;
}

interface PreferencesContextType {
  preferences: UserPreferences;
  updateDisplayPreferences: (updates: Partial<DisplayPreferences>) => void;
  updateNotificationPreferences: (updates: Partial<NotificationPreferences>) => void;
  formatCurrency: (amount: number, options?: { compact?: boolean }) => string;
  getCurrencySymbol: () => string;
}

const defaultPreferences: UserPreferences = {
  display: {
    currency: 'USD',
    theme: 'dark',
    compactMode: false,
    accentColor: 'teal',
    fontSize: 'medium',
    chartColorScheme: 'classic',
    sidebarCollapsed: false,
    animationIntensity: 'full',
    showPriceTicker: true,
  },
  notifications: {
    priceAlerts: true,
    portfolioUpdates: true,
    marketNews: false,
    emailDigest: true,
  },
};

const PREFERENCES_KEY = 'userPreferences';

const currencyConfig: Record<CurrencyCode, { symbol: string; locale: string }> = {
  USD: { symbol: '$', locale: 'en-US' },
  EUR: { symbol: '€', locale: 'de-DE' },
  GBP: { symbol: '£', locale: 'en-GB' },
  ZAR: { symbol: 'R', locale: 'en-ZA' },
};

/** Apply all display preferences as data-* attributes on <html>. Called on every display change. */
function applyThemeAttributes(display: DisplayPreferences) {
  const root = document.documentElement;
  root.setAttribute('data-theme', display.theme);
  root.setAttribute('data-accent', display.accentColor);
  root.setAttribute('data-font-size', display.fontSize);
  root.setAttribute('data-animations', display.animationIntensity);
}

/** Map DB snake_case display fields → camelCase context state. */
function dbDisplayToLocal(db: DisplayPreferencesAPI): Partial<DisplayPreferences> {
  return {
    currency: db.currency as CurrencyCode,
    theme: db.theme as ThemeMode,
    compactMode: db.compact_mode,
    accentColor: db.accent_color as AccentColor,
    fontSize: db.font_size as FontSize,
    chartColorScheme: db.chart_color_scheme as ChartColorScheme,
    sidebarCollapsed: db.sidebar_collapsed,
    animationIntensity: db.animation_intensity as AnimationIntensity,
    showPriceTicker: db.show_price_ticker,
  };
}

/** Map camelCase context display state → DB snake_case for PUT /preferences. */
function localDisplayToDb(display: DisplayPreferences) {
  return {
    currency: display.currency,
    theme: display.theme,
    compact_mode: display.compactMode,
    accent_color: display.accentColor,
    font_size: display.fontSize,
    chart_color_scheme: display.chartColorScheme,
    sidebar_collapsed: display.sidebarCollapsed,
    animation_intensity: display.animationIntensity,
    show_price_ticker: display.showPriceTicker,
  };
}

const PreferencesContext = createContext<PreferencesContextType | undefined>(undefined);

export const PreferencesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  // Track whether DB prefs have been loaded for this session to avoid redundant fetches.
  const dbLoadedRef = useRef(false);
  // Mirror of current display state for use in callbacks without stale-closure risk.
  const displayRef = useRef<DisplayPreferences>(defaultPreferences.display);

  const [preferences, setPreferences] = useState<UserPreferences>(() => {
    const saved = localStorage.getItem(PREFERENCES_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return {
          display: { ...defaultPreferences.display, ...parsed.display },
          notifications: { ...defaultPreferences.notifications, ...parsed.notifications },
        };
      } catch {
        return defaultPreferences;
      }
    }
    return defaultPreferences;
  });

  // Keep displayRef in sync so callbacks always see current display state.
  useEffect(() => {
    displayRef.current = preferences.display;
  }, [preferences.display]);

  // Apply theme data attrs to <html> immediately and on every display change
  useEffect(() => {
    applyThemeAttributes(preferences.display);
  }, [preferences.display]);

  // Persist to localStorage on every change
  useEffect(() => {
    localStorage.setItem(PREFERENCES_KEY, JSON.stringify(preferences));
  }, [preferences]);

  // On login: fetch preferences from DB and merge (DB wins over localStorage defaults).
  // dbLoadedRef resets on logout so the next login re-fetches fresh values.
  useEffect(() => {
    if (!isAuthenticated) {
      dbLoadedRef.current = false;
      return;
    }
    if (dbLoadedRef.current) return;
    dbLoadedRef.current = true;

    api.getPreferences().then((data) => {
      if (!data?.display) return;
      setPreferences(prev => ({
        ...prev,
        display: { ...prev.display, ...dbDisplayToLocal(data.display) },
      }));
    }).catch(() => {
      // Non-fatal — fall back to localStorage values
    });
  }, [isAuthenticated]);

  const updateDisplayPreferences = useCallback((updates: Partial<DisplayPreferences>) => {
    // Pure state update — no side effects inside the updater.
    setPreferences(prev => ({ ...prev, display: { ...prev.display, ...updates } }));
    // Persist to DB — fire and forget, errors are non-fatal.
    // Uses displayRef to get current state without stale-closure risk.
    if (isAuthenticated) {
      const merged = { ...displayRef.current, ...updates };
      api.updatePreferences({ display: localDisplayToDb(merged) }).catch(() => {});
    }
  }, [isAuthenticated]);

  const updateNotificationPreferences = useCallback((updates: Partial<NotificationPreferences>) => {
    setPreferences(prev => ({
      ...prev,
      notifications: { ...prev.notifications, ...updates },
    }));
  }, []);

  const formatCurrency = useCallback((amount: number, options?: { compact?: boolean }): string => {
    const { currency } = preferences.display;
    const config = currencyConfig[currency];

    if (options?.compact && Math.abs(amount) >= 1000) {
      const formatter = new Intl.NumberFormat(config.locale, {
        style: 'currency',
        currency,
        notation: 'compact',
        maximumFractionDigits: 1,
      });
      return formatter.format(amount);
    }

    const formatter = new Intl.NumberFormat(config.locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return formatter.format(amount);
  }, [preferences.display.currency]);

  const getCurrencySymbol = useCallback((): string => {
    return currencyConfig[preferences.display.currency].symbol;
  }, [preferences.display.currency]);

  return (
    <PreferencesContext.Provider
      value={{
        preferences,
        updateDisplayPreferences,
        updateNotificationPreferences,
        formatCurrency,
        getCurrencySymbol,
      }}
    >
      {children}
    </PreferencesContext.Provider>
  );
};

export const usePreferences = (): PreferencesContextType => {
  const context = useContext(PreferencesContext);
  if (!context) {
    throw new Error('usePreferences must be used within a PreferencesProvider');
  }
  return context;
};
